import { initializeApp } from 'firebase/app';
import { getDatabase, ref as ref_database, get, child } from "firebase/database";
import {
    getFirestore,
    collection,
    getDocs,
    query,
    where,
    addDoc,
    serverTimestamp,
    limit
} from "firebase/firestore";
import {
    getAuth,
    signInWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
    GoogleAuthProvider,
    signInWithPopup
} from 'firebase/auth';

// Firebase configuration from environment variables
const firebaseConfig = {
    apiKey: process.env.VITE_FIREBASE_API_KEY,
    authDomain: process.env.VITE_FIREBASE_AUTH_DOMAIN,
    databaseURL: process.env.VITE_FIREBASE_DATABASE_URL,
    projectId: process.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: process.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.VITE_FIREBASE_APP_ID
};

// Validate that all required environment variables are present
const requiredEnvVars = {
    'VITE_FIREBASE_API_KEY': process.env.VITE_FIREBASE_API_KEY,
    'VITE_FIREBASE_AUTH_DOMAIN': process.env.VITE_FIREBASE_AUTH_DOMAIN,
    'VITE_FIREBASE_DATABASE_URL': process.env.VITE_FIREBASE_DATABASE_URL,
    'VITE_FIREBASE_PROJECT_ID': process.env.VITE_FIREBASE_PROJECT_ID,
    'VITE_FIREBASE_STORAGE_BUCKET': process.env.VITE_FIREBASE_STORAGE_BUCKET,
    'VITE_FIREBASE_MESSAGING_SENDER_ID': process.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    'VITE_FIREBASE_APP_ID': process.env.VITE_FIREBASE_APP_ID
};

const missingEnvVars = Object.keys(requiredEnvVars).filter(key => !requiredEnvVars[key]);

if (missingEnvVars.length > 0) {
    console.error('Missing required environment variables:', missingEnvVars);
    console.error('Please check your .env file and ensure all Firebase credentials are set.');
}

const app = initializeApp(firebaseConfig);
const firestore = getFirestore(app);
const auth = getAuth(app);
const googleProvider = new GoogleAuthProvider();
const DEFAULT_EVENT_LOOKBACK_DAYS = 1;
const FALLBACK_EVENT_LIMIT = 1000;

// DONE: fetch geojson data from firebase
export async function getGeoJsonData(databaseName) {
    // Get a database reference to our countries
    // databaseName is the country code like sy for Syr and isr for isra
    try {
        console.log("Getting geoJSON data: ");
        const db = getDatabase(app);
        const dbRef = ref_database(db);
        // const snapshot = await get(child(dbRef, 'Maps/' + databaseName));
        const snapshot = await get(child(dbRef, 'zones/' + databaseName.toLowerCase() + '/control_zones/geojson'));

        const jsonData = snapshot.val();
        return jsonData;
    } catch (error) {
        throw error;
    }
}

// get Link
export async function getLinkData() {
    const db = getDatabase(app);
    const dbRef = ref_database(db);
    var link = "";
    const snapshot = await get(child(dbRef, `link/Feb_2024`));
    console.log("SHEETS link ",snapshot.val());
    
    return snapshot.val();
}

// fethc geo data from firebase
// TODO; Problem fetching too many docuemnt up to 50k docuemnt a day is not FREE
export async function fetchAllGeoData(countryFilter) {
    var currentData = new Date();
    var oneWeekAgoDate = currentData.setDate(currentData.getDate() - 7);
    // const querySnapshot =  await getDocs(collection(getFirestore(app), "geo_data"));
    let data = [];
    const q = query(collection(firestore, "events"), where("country", "==", countryFilter)
        , where("event_date", ">=", oneWeekAgoDate));
    const querySnapshot = await getDocs(q);
    querySnapshot.forEach((doc) => {
        // console.log(doc.id, " => ", doc.data());
        data.push(doc.data());
    });
    return data;
}

export async function fetchEventsForCountry({ country, startDate, endDate } = {}) {
    const trimmedCountry = typeof country === 'string' ? country.trim() : '';

    if (!trimmedCountry) {
        console.warn('fetchEventsForCountry called without a valid country name');
        return [];
    }

    const normalizedCountry = trimmedCountry.toLowerCase();
    let startBound = ensureISODate(startDate);
    let endBound = ensureISODate(endDate);

    if (!endBound) {
        endBound = ensureISODate(new Date());
    }

    if (!startBound && endBound) {
        const endDateObject = dateFromISO(endBound) || new Date();
        const lookbackDays = Math.max(DEFAULT_EVENT_LOOKBACK_DAYS - 1, 0);
        const lookbackDate = subtractDaysUTC(endDateObject, lookbackDays);
        startBound = ensureISODate(lookbackDate);
    }

    if (startBound && endBound && startBound > endBound) {
        const temp = startBound;
        startBound = endBound;
        endBound = temp;
    }

    const eventsRef = collection(firestore, 'events');
    const queryAttempts = buildCountryQueryCandidates(trimmedCountry, normalizedCountry);
    const results = [];
    let sawIndexError = false;
    let lastNonIndexError = null;

    for (const attempt of queryAttempts) {
        if (!attempt.value) {
            continue;
        }

        try {
            const snapshot = await getEventsSnapshot(eventsRef, attempt.fieldPath, attempt.value, startBound, endBound);
            if (!snapshot.empty) {
                snapshot.docs.forEach(doc => {
                    results.push({
                        id: doc.id,
                        ...doc.data()
                    });
                });
                return dedupeDocs(results);
            }
        } catch (error) {
            if (requiresCompositeIndex(error)) {
                sawIndexError = true;
                continue;
            }

            lastNonIndexError = error;
            console.error(`Error querying events for ${attempt.fieldPath} = ${attempt.value}:`, error?.message || error);
        }
    }

    try {
        const fallbackDocs = await fetchEventsByDateRange(eventsRef, startBound, endBound);
        if (fallbackDocs.length) {
            const filtered = fallbackDocs.filter(doc => recordMatchesCountry(doc, normalizedCountry));
            if (filtered.length) {
                return dedupeDocs(filtered);
            }
        }
    } catch (error) {
        if (requiresCompositeIndex(error)) {
            sawIndexError = true;
        } else {
            lastNonIndexError = error;
        }
    }

    if (lastNonIndexError) {
        throw lastNonIndexError;
    }

    if (sawIndexError) {
        console.warn('Firestore composite index missing for one or more event queries. Consider adding an index on (country_normalized, event_date).');
    }

    return [];
}

// TODO: fetch countries list
export async function fetchDefaultMap() {
    const db = getDatabase(app);
    const dbRef = ref_database(db, 'defaultMap');
    const snapshot = await get(dbRef);
    if(snapshot.exists()){
        return snapshot.val();
    }
    else {
        console.log("No data available for Default Map!");
        return{};
    }
}

// Authentication helpers
export function onAuthStateChange(callback) {
    return onAuthStateChanged(auth, callback);
}

export function signInWithEmail(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
}

export function signInWithGooglePopup() {
    return signInWithPopup(auth, googleProvider);
}

export function signOutCurrentUser() {
    return signOut(auth);
}

// Firestore helpers
export async function createGeoEventDocument(payload) {
    const title = sanitizeText(payload.title);
    const notesEnglish = sanitizeText(payload.rephrase_english);
    const notesArabic = sanitizeText(payload.rephrase_arabic);
    const notesTurkish = sanitizeText(payload.rephrase_turkish);
    const eventType = sanitizeText(payload.event_type);
    const eventTypeNormalized = eventType ? eventType.toLowerCase() : null;
    const trimmedCountry = sanitizeText(payload.country);
    const trimmedCountryArabic = sanitizeText(payload.country_arabic);
    const normalizedCountry = trimmedCountry ? trimmedCountry.toLowerCase() : null;
    const latitude = normalizeCoordinate(payload.latitude);
    const longitude = normalizeCoordinate(payload.longitude);
    const killed = normalizeCount(payload.killed);
    const injured = normalizeCount(payload.injured);
    const eventDateIso = ensureISODate(payload.event_date) || ensureISODate(new Date());

    const cleanedPayload = {
        title,
        notes_en: notesEnglish,
        notes_ar: notesArabic,
        notes_tr: notesTurkish,
        event_type: eventType,
        event_type_normalized: eventTypeNormalized,
        event_date: eventDateIso,
        country: trimmedCountry,
        country_normalized: normalizedCountry,
        casualties: {
            killed,
            injured,
        },
        geolocation: {
            country: trimmedCountry,
            country_ar: trimmedCountryArabic,
            country_normalized: normalizedCountry,
            city: sanitizeText(payload.city),
            city_ar: sanitizeText(payload.city_arabic),
            neighborhood: sanitizeText(payload.neighborhood),
            neighborhood_ar: sanitizeText(payload.neighborhood_arabic),
            street: sanitizeText(payload.street),
            street_ar: sanitizeText(payload.street_arabic),
            location_name: sanitizeText(payload.location_name),
            location_name_ar: sanitizeText(payload.location_name_arabic),
            latitude,
            longitude,
        },
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
    };

    return addDoc(collection(firestore, 'events'), cleanedPayload);
}

export { firestore, auth };

function buildCountryQueryCandidates(trimmedCountry, normalizedCountry) {
    const attempts = [];
    const seen = new Set();

    const addCandidate = (fieldPath, value) => {
        if (typeof value !== 'string') {
            return;
        }
        const candidateValue = value.trim();
        if (!candidateValue) {
            return;
        }
        const key = `${fieldPath}::${candidateValue}`;
        if (seen.has(key)) {
            return;
        }
        seen.add(key);
        attempts.push({ fieldPath, value: candidateValue });
    };

    if (normalizedCountry) {
        addCandidate('country_normalized', normalizedCountry);
        addCandidate('country', normalizedCountry);
        addCandidate('geolocation.country_normalized', normalizedCountry);
        addCandidate('geolocation.country', normalizedCountry);
    }

    if (trimmedCountry) {
        addCandidate('country', trimmedCountry);
        addCandidate('geolocation.country', trimmedCountry);
        addCandidate('country', trimmedCountry.toUpperCase());
        addCandidate('geolocation.country', trimmedCountry.toUpperCase());
    }

    return attempts;
}

function requiresCompositeIndex(error) {
    if (!error) {
        return false;
    }

    if (error.code === 'failed-precondition' && typeof error.message === 'string' && error.message.includes('index')) {
        return true;
    }

    return typeof error.message === 'string' && error.message.toLowerCase().includes('index');
}

async function fetchEventsByDateRange(eventsRef, startBound, endBound) {
    const constraints = [];

    if (startBound) {
        constraints.push(where('event_date', '>=', startBound));
    }

    if (endBound) {
        constraints.push(where('event_date', '<=', endBound));
    }

    constraints.push(limit(FALLBACK_EVENT_LIMIT));

    const fallbackQuery = query(eventsRef, ...constraints);
    const snapshot = await getDocs(fallbackQuery);
    return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
    }));
}

function recordMatchesCountry(record, targetNormalized) {
    if (!targetNormalized) {
        return false;
    }

    const candidates = [
        record?.country,
        record?.country_normalized,
        record?.geolocation?.country,
        record?.geolocation?.country_normalized,
    ];

    return candidates.some(value => typeof value === 'string' && value.trim().toLowerCase() === targetNormalized);
}

function dedupeDocs(documents) {
    const map = new Map();
    documents.forEach(entry => {
        if (entry && entry.id) {
            map.set(entry.id, entry);
        }
    });
    return Array.from(map.values());
}

function sanitizeText(value) {
    if (typeof value === 'string') {
        const trimmed = value.trim();
        return trimmed ? trimmed : null;
    }
    return value ?? null;
}

function normalizeCoordinate(value) {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed) {
            const parsed = Number.parseFloat(trimmed);
            return Number.isFinite(parsed) ? parsed : null;
        }
    }
    return null;
}

function normalizeCount(value) {
    if (value === null || typeof value === 'undefined' || value === '') {
        return null;
    }
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
}

async function getEventsSnapshot(eventsRef, fieldPath, fieldValue, startBound, endBound) {
    const constraints = [where(fieldPath, '==', fieldValue)];

    if (startBound) {
        constraints.push(where('event_date', '>=', startBound));
    }

    if (endBound) {
        constraints.push(where('event_date', '<=', endBound));
    }

    const eventsQuery = query(eventsRef, ...constraints);
    return getDocs(eventsQuery);
}

function ensureISODate(value) {
    if (!value) {
        return null;
    }

    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) {
            return null;
        }

        if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
            return trimmed;
        }

        const parsed = new Date(trimmed);
        if (!Number.isNaN(parsed.getTime())) {
            return formatISODate(parsed);
        }

        return null;
    }

    if (value instanceof Date) {
        return formatISODate(value);
    }

    if (typeof value === 'number') {
        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime()) ? null : formatISODate(parsed);
    }

    if (value && typeof value.toDate === 'function') {
        return formatISODate(value.toDate());
    }

    return null;
}

function formatISODate(date) {
    const utcYear = date.getUTCFullYear();
    const utcMonth = String(date.getUTCMonth() + 1).padStart(2, '0');
    const utcDay = String(date.getUTCDate()).padStart(2, '0');
    return `${utcYear}-${utcMonth}-${utcDay}`;
}

function subtractDaysUTC(date, days) {
    const base = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
    base.setUTCDate(base.getUTCDate() - days);
    return base;
}

function dateFromISO(isoValue) {
    if (!isoValue) {
        return null;
    }

    const parsed = new Date(isoValue);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}